import { Fragment, useMemo, useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ButtonPrimary, ButtonSecondary } from '@components/button';
import { ErrorMessage } from '@components/error/src/error-message';
import { LabelPrimary } from '@components/label';
import { Listbox } from '@components/listbox';
import { ModalV2 } from '@components/modals/modal-v2';
import { TextareaCounter } from '@components/textarea-counter';
import { LANGUAGES } from '@domain/language';
import { getDynamicPlaceholder } from '@utils/get-dynamic-placeholder/get-dynamic-placeholder';

import { EditFieldModalProps, Form, OFFER_NAME_MAX_LENGTH } from './edit-field-modal.types';

export const EditFieldModal = ({
  isOpen,
  onClose,
  onSave,
  defaultValue,
  isEditDisabled,
  labels,
}: EditFieldModalProps) => {
  const { t, i18n } = useTranslation();

  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  const methods = useForm<Form>({
    defaultValues: {
      nameLanguage: i18n.language,
      name: defaultValue,
    },
    mode: 'onChange',
  });

  const {
    register,
    watch,
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
  } = methods;

  const onSubmit: SubmitHandler<Form> = async (form) => {
    onSave(form.name);
  };

  const isSubmitButtonDisabled = useMemo(() => {
    return !isDirty || !isValid;
  }, [isDirty, isValid]);

  const languageOptions = useMemo(
    () =>
      LANGUAGES.map(({ id, value }) => ({
        id,
        value: t(value),
      })),
    [t]
  );

  const handleClose = () => {
    onClose();
  };

  return (
    <ModalV2 isOpen={isOpen} onClose={handleClose} variant='md'>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='mx-9 mt-5'>
            <div className='flex justify-between items-center'>
              <LabelPrimary htmlFor='name' hasError={!!errors.name}>
                {labels?.title ? t(labels.title) : t('offers:admissionOfferNameLabel')}
              </LabelPrimary>
              <div className='flex justify-end'>
                <div className='w-36'>
                  <Controller
                    name='nameLanguage'
                    control={control}
                    render={({ field }) => (
                      <Listbox
                        {...field}
                        onChange={(value) => {
                          field.onChange(value);
                          setSelectedLanguage(value as string);
                        }}
                        options={languageOptions}
                        zIndexClassName='z-[9999]'
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            {LANGUAGES.map((lang) => {
              if (lang.id !== selectedLanguage) {
                return null;
              }

              const name = `name.${lang.id}`;

              const hasError =
                // eslint-disable-next-line
                // @ts-ignore
                !!errors.name?.[lang.id] || false;

              // eslint-disable-next-line
              // @ts-ignore
              const nameField = watch(name);
              const length = nameField?.length || 0;

              // eslint-disable-next-line
              // @ts-ignore
              const textAreaRegister = register(name, {
                maxLength: OFFER_NAME_MAX_LENGTH,
              });

              return (
                <Fragment key={`name-${lang.id}`}>
                  <div className={'mt-1'}>
                    <TextareaCounter
                      id={name}
                      rows={3}
                      data-testid='edit-field-modal--text-area'
                      placeholder={getDynamicPlaceholder(t('offers:admissionOfferNamePlaceholder'), defaultValue)}
                      length={length}
                      maxLength={OFFER_NAME_MAX_LENGTH}
                      hasError={hasError}
                      charactersText={t('common:characters')}
                      disabled={isEditDisabled}
                      readOnly={isEditDisabled}
                      {...textAreaRegister}
                    />
                  </div>
                  <ErrorMessage name={name} />
                </Fragment>
              );
            })}
          </div>

          <div className='flex flex-row items-center justify-end mx-7 my-5'>
            <ButtonSecondary data-test-id='edit-field-modal--cancel-button' onClick={handleClose}>
              {labels?.cancelButton ? t(labels.cancelButton) : t('common:cancel')}
            </ButtonSecondary>
            <ButtonPrimary
              data-testid='edit-field-modal--save-button'
              type='submit'
              className='ml-6'
              disabled={isSubmitButtonDisabled}>
              {labels?.saveButton ? t(labels.saveButton) : t('common:save')}
            </ButtonPrimary>
          </div>
        </form>
      </FormProvider>
    </ModalV2>
  );
};
